import React, { useCallback, useEffect, useState } from 'react';
import Spinner from './Spinner';
import './RechargeAllowanceSettings.css';

const TravelReplacementSettings = ({ backendService, dojoContext = false }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const next = await backendService.getTravelReplacementAllowance();
      setStatus(next);
      setDraft(String(next?.total ?? 0));
    } catch (_) {
      setError('Could not load travel replacement settings.');
    } finally {
      setLoading(false);
    }
  }, [backendService]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    const handler = () => refresh();
    window.addEventListener('regimen:travelReplacements:changed', handler);
    return () => window.removeEventListener('regimen:travelReplacements:changed', handler);
  }, [refresh]);

  const save = async () => {
    if (status?.can_edit === false) {
      setError('Only the dojo chief can change this allowance.');
      setEditing(false);
      return;
    }
    const total = Number(draft);
    if (!Number.isInteger(total) || total < 0 || total > 365) {
      setError('Enter a whole number from 0 to 365.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const next = await backendService.updateTravelReplacementAllowance(total);
      setStatus(next);
      setDraft(String(next?.total ?? total));
      setEditing(false);
    } catch (_) {
      setError('Could not save travel replacement allowance.');
    } finally {
      setSaving(false);
    }
  };

  const title = dojoContext ? 'Your Travel Days' : 'Travel Days';

  if (loading) {
    return (
      <section className="rest-days-section" aria-labelledby="travel-settings-title">
        <h4 id="travel-settings-title" className="rest-days-title"><span aria-hidden="true">🧳</span> {title}</h4>
        <div className="rest-days-loading" role="status" aria-live="polite">
          <Spinner />
          <span>Loading travel replacements…</span>
        </div>
      </section>
    );
  }

  const total = status?.total ?? 0;
  const used = status?.used ?? 0;
  const remaining = status?.remaining ?? Math.max(0, total - used);
  const enabled = status?.enabled !== false && total > 0;
  const dojoOwned = status?.scope === 'dojo';
  const canEdit = status?.can_edit !== false;
  const ownerName = status?.owner_name || 'your dojo';
  const recoveryProgress = Math.max(0, Math.min(1, Number(status?.recovery_progress) || 0));
  const refillDaysPerToken = Number(status?.refill_days_per_token) || (total > 0 ? 365.25 / total : 0);
  const refillCadenceDays = refillDaysPerToken > 0 ? Math.max(1, Math.round(refillDaysPerToken)) : 0;
  const recoveryPercent = remaining < total
    ? Math.min(99, Math.floor(recoveryProgress * 100))
    : 100;
  const daysUntilNext = remaining < total && refillDaysPerToken > 0
    ? Math.max(1, Math.ceil((1 - recoveryProgress) * refillDaysPerToken))
    : 0;

  return (
    <section className="rest-days-section" aria-labelledby="travel-settings-title">
      <div className="rest-days-header">
        <h4 id="travel-settings-title" className="rest-days-title"><span aria-hidden="true">🧳</span> {title}</h4>
        <div className="rest-days-summary">
          {enabled ? (
            <>
              <span className="rest-days-remaining">{remaining}</span>
              <span className="rest-days-total"> / {total} available</span>
            </>
          ) : (
            <span className="rest-days-total">Disabled</span>
          )}
        </div>
      </div>

      {(dojoContext || dojoOwned) && (
        <div className="rest-days-personal-scope">
          {dojoOwned
            ? <>Allowance established by {ownerName} · your remaining balance and recovery are personal to you.</>
            : 'Personal to you · replacements never modify the shared dojo plan.'}
        </div>
      )}

      <p className="rest-days-help">
        {dojoOwned && !enabled
          ? `${ownerName} has not enabled Travel Replacements.`
          : <>Use one on an unexpired workout when travel prevents the programmed session. Completion preserves consistency credit, not the original training effect. {total > 0
            ? <>You regain about one travel day every {refillCadenceDays} day{refillCadenceDays === 1 ? '' : 's'} ({total} per year), capped at {total}.</>
            : 'Set an annual allowance to start recharging travel replacements.'}</>}
      </p>

      {enabled && remaining < total && (
        <div className="rest-days-recovery">
          <div className="rest-days-recovery-heading">
            <span>Next travel day</span>
            <strong>{recoveryPercent}% recharged</strong>
          </div>
          <div
            className="rest-days-recovery-progress"
            role="progressbar"
            aria-label="Progress toward the next available travel day"
            aria-valuemin="0"
            aria-valuemax="100"
            aria-valuenow={recoveryPercent}
            aria-valuetext={`${recoveryPercent} percent restored; about ${daysUntilNext} days until the next travel day`}
          >
            <span style={{ width: `${recoveryPercent}%` }} />
          </div>
          <small>About {daysUntilNext} day{daysUntilNext === 1 ? '' : 's'} until one full travel day is available.</small>
        </div>
      )}

      {(enabled || canEdit) && (!editing || !canEdit ? (
        <div className="rest-days-row">
          {enabled && (
            <span className="rest-days-used">
              {used === 0 ? 'Fully recharged' : `${used} travel day${used === 1 ? '' : 's'} currently unavailable`}
            </span>
          )}
          {canEdit && (
            <button type="button" className="rest-days-btn" onClick={() => setEditing(true)}>
              {dojoOwned ? 'Change allowance' : 'Change quota'}
            </button>
          )}
        </div>
      ) : (
        <div className="rest-days-edit">
          <label className="rest-days-label" htmlFor="travel-replacement-quota">Replacements per year</label>
          <div className="rest-days-edit-row">
            <input
              id="travel-replacement-quota"
              type="number"
              min="0"
              max="365"
              value={draft}
              onChange={event => setDraft(event.target.value)}
              className="rest-days-input"
              disabled={saving}
            />
            <button type="button" className="rest-days-btn primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
            <button type="button" className="rest-days-btn" onClick={() => { setEditing(false); setDraft(String(total)); setError(''); }} disabled={saving}>Cancel</button>
          </div>
        </div>
      ))}

      {error && <div className="rest-days-error" role="alert">{error}</div>}
    </section>
  );
};

export default TravelReplacementSettings;
